import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Check, X, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UsernameEditorProps {
  currentUsername: string | null;
  userId: string;
  onUsernameChange?: (newUsername: string) => void;
}

export const UsernameEditor = ({ currentUsername, userId, onUsernameChange }: UsernameEditorProps) => {
  const [username, setUsername] = useState(currentUsername || "");
  const [availability, setAvailability] = useState<"available" | "taken" | "checking" | "idle" | "invalid">("idle");
  const [saving, setSaving] = useState(false);

  const validate = (val: string) => /^[a-zA-Z0-9_]{3,30}$/.test(val) && !/__/.test(val);

  const checkAvailability = useCallback(
    async (val: string) => {
      if (!validate(val)) {
        setAvailability("invalid");
        return;
      }
      if (val === currentUsername) {
        setAvailability("idle");
        return;
      }
      setAvailability("checking");
      const { data } = await supabase
        .from("profiles")
        .select("id")
        .eq("username", val)
        .neq("id", userId)
        .maybeSingle();
      setAvailability(data ? "taken" : "available");
    },
    [currentUsername, userId]
  );

  useEffect(() => {
    if (!username || username === currentUsername) {
      setAvailability("idle");
      return;
    }
    const timer = setTimeout(() => checkAvailability(username), 300);
    return () => clearTimeout(timer);
  }, [username, checkAvailability, currentUsername]);

  const handleSave = async () => {
    if (availability !== "available") return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ username })
      .eq("id", userId);
    setSaving(false);
    if (error) {
      if (error.code === "23505") {
        setAvailability("taken");
        toast.error("Username already taken");
      } else {
        toast.error("Failed to update username");
      }
      return;
    }
    toast.success("Username updated!");
    onUsernameChange?.(username);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="username">Username</Label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none">@</span>
          <Input
            id="username"
            value={username}
            onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
            className="pl-6"
            placeholder="your_username"
            maxLength={30}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {availability === "checking" && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            {availability === "available" && <Check className="h-4 w-4 text-green-500" />}
            {availability === "taken" && <X className="h-4 w-4 text-destructive" />}
            {availability === "invalid" && <X className="h-4 w-4 text-orange-500" />}
          </div>
        </div>
        <Button
          onClick={handleSave}
          disabled={availability !== "available" || saving}
          size="sm"
          className="shrink-0"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
        </Button>
      </div>
      {availability === "invalid" && (
        <p className="text-xs text-orange-500">3-30 characters, letters, numbers, and underscores only.</p>
      )}
      {availability === "taken" && (
        <p className="text-xs text-destructive">This username is already taken.</p>
      )}
      {availability === "available" && (
        <p className="text-xs text-green-500">Username is available!</p>
      )}
      <p className="text-xs text-muted-foreground">This is your public display name on shared designs.</p>
    </div>
  );
};
